import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Building2,
  Wallet,
  Percent,
  Banknote,
  RefreshCw,
  AlertCircle,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { BASE_URL } from "../../../config/config";

// If BASE_URL is already imported from your config, use that instead.
// Example:
// import { BASE_URL } from "../config";


const UserPortFolio = () => {
  const [portfolio, setPortfolio] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate()

  const fetchPortfolio = async () => {
    setIsLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("rwatoken");

      const response = await axios.get(
        `${BASE_URL}/investor/me/portfolio`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Portfolio API response:", response?.data);

      setPortfolio(response?.data);
    } catch (error) {
      console.log("Portfolio API error:", error);

      setError(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Unable to load your portfolio. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const handlePortfolioDetails = (id)=>{
    if(!id){
      message.error("Something Went Wrong");
      return;
    }
    navigate("/customer/portfolio/details",
      {state: {
              id: id
            }})
  }

  /* =========================================================
     PORTFOLIO CALCULATIONS
  ========================================================= */

  const holdings = portfolio?.holdings || [];

  const portfolioStats = useMemo(() => {
    const totalProperties = holdings.length;

    const totalBlocks = holdings.reduce(
      (total, item) => total + Number(item?.blocks_held || 0),
      0
    );

    const totalOwnership = holdings.reduce(
      (total, item) => total + Number(item?.ownership_pct || 0),
      0
    );

    const totalRent = holdings.reduce(
      (total, item) => total + Number(item?.rent_earned_aed || 0),
      0
    );

    return {
      totalProperties,
      totalBlocks,
      totalOwnership,
      totalRent,
    };
  }, [holdings]);

  const formatNumber = (value) => {
    return Number(value || 0).toLocaleString("en-US", {
      maximumFractionDigits: 2,
    });
  };

  const formatAED = (value) => {
    return `AED ${formatNumber(value)}`;
  };

  /* =========================================================
     LOADING
  ========================================================= */

  if (isLoading) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-[#fdfbf5] px-4 py-8 sm:px-6 lg:px-8">
        {/* Background blur */}
        <div className="relative z-10 mx-auto max-w-[1180px]">

  {/* BACK BUTTON */}
 

  {/* PAGE HEADER */}
 

  {/* rest of portfolio */}
</div>

        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="
              absolute
              -left-40
              -top-32
              h-[450px]
              w-[450px]
              rounded-full
              bg-[#e3ecd6]/70
              blur-[120px]
            "
          />

          <div
            className="
              absolute
              -right-40
              top-[20%]
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#eef3e7]/80
              blur-[130px]
            "
          />

          <div
            className="
              absolute
              bottom-[-220px]
              left-[30%]
              h-[500px]
              w-[500px]
              rounded-full
              bg-[#e3ecd6]/60
              blur-[120px]
            "
          />

          <div className="absolute inset-0 bg-white/20 backdrop-blur-[2px]" />
        </div>


        <div className="relative z-10 mx-auto max-w-[1180px]">

          <div className="mb-8">
            <div className="h-3 w-28 animate-pulse rounded-full bg-[#e3ecd6]" />

            <div className="mt-4 h-10 w-[330px] max-w-full animate-pulse rounded-xl bg-[#e3ecd6]" />

            <div className="mt-3 h-3 w-[280px] max-w-full animate-pulse rounded-full bg-[#eef3e7]" />
          </div>

          {/* Stats skeleton */}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="
                  h-[130px]
                  animate-pulse
                  rounded-[20px]
                  border
                  border-white
                  bg-white/78
                  shadow-[0_12px_30px_rgba(27,82,108,0.06)]
                "
              />
            ))}
          </div>

          {/* Holdings skeleton */}

          <div className="mt-8 rounded-[24px] border border-white bg-white/72 p-5 shadow-[0_20px_55px_rgba(76,122,46,0.08)]">
            <div className="h-5 w-36 animate-pulse rounded bg-[#e3ecd6]" />

            <div className="mt-5 space-y-3">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-[105px] animate-pulse rounded-[16px] bg-[#eef3e7]/70"
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* =========================================================
     ERROR
  ========================================================= */

  if (error) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-[#fdfbf5] px-4 py-8 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute inset-0">
          <div
            className="
              absolute
              -left-32
              top-0
              h-[400px]
              w-[400px]
              rounded-full
              bg-[#e3ecd6]/60
              blur-[110px]
            "
          />

          <div
            className="
              absolute
              -right-32
              bottom-0
              h-[450px]
              w-[450px]
              rounded-full
              bg-[#eef3e7]/70
              blur-[120px]
            "
          />
        </div>

        <div className="relative z-10 flex min-h-[70vh] items-center justify-center">
          <div
            className="
              w-full
              max-w-[420px]
              rounded-[26px]
              border
              border-white
              bg-white/80
              p-8
              text-center
              shadow-[0_25px_70px_rgba(76,122,46,0.12)]
              backdrop-blur-xl
            "
          >
            <div
              className="
                mx-auto
                flex
                h-14
                w-14
                items-center
                justify-center
                rounded-2xl
                bg-[#f6eee8]
                text-[#9b624f]
              "
            >
              <AlertCircle size={25} />
            </div>

            <h2 className="mt-5 text-[20px] font-bold tracking-[-0.025em] text-[#1f2318]">
              Unable to load portfolio
            </h2>

            <p className="mt-2 text-[12px] leading-[1.7] text-[#66705c]">
              {error}
            </p>

            <button
              type="button"
              onClick={fetchPortfolio}
              className="
                mt-6
                inline-flex
                items-center
                gap-2
                rounded-full
                bg-[#4c7a2e]
                px-5
                py-2.5
                text-[11px]
                font-bold
                text-white
                shadow-[0_10px_25px_rgba(76,122,46,0.18)]
                transition-all
                duration-300
                hover:-translate-y-0.5
                hover:bg-[#375c20]
              "
            >
              <RefreshCw size={13} />
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* =========================================================
     MAIN PAGE
  ========================================================= */

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#fdfbf5] px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
      {/* =====================================================
          BACKGROUND BLUR
      ===================================================== */}

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="
            absolute
            -left-40
            -top-40
            h-[480px]
            w-[480px]
            rounded-full
            bg-[#e3ecd6]/70
            blur-[120px]
          "
        />

        <div
          className="
            absolute
            right-[-180px]
            top-[20%]
            h-[520px]
            w-[520px]
            rounded-full
            bg-[#e5f1f5]/75
            blur-[130px]
          "
        />

        <div
          className="
            absolute
            bottom-[-240px]
            left-[30%]
            h-[520px]
            w-[520px]
            rounded-full
            bg-[#e3ecd6]/55
            blur-[125px]
          "
        />

        <div className="absolute inset-0 bg-white/15" />
      </div>

      {/* =====================================================
          CONTENT
      ===================================================== */}

      <div className="relative z-10 mx-auto max-w-[1180px]">
        {/* ===================================================
            PAGE HEADER
        =================================================== */}
         <div
    className="
      mb-8
      flex
      flex-col
      gap-5
      sm:mb-10
      md:flex-row
      md:items-end
      md:justify-between
    "
  >
 <div className="mb-5">
    <button
      type="button"
      onClick={() => navigate(-1)}
      className="
        group
        inline-flex
        items-center
        gap-2
        rounded-full
        border
        border-white/80
        bg-white/72
        px-4
        py-2
        text-[11px]
        font-bold
        text-[#4c7a2e]
        shadow-[0_8px_22px_rgba(76,122,46,0.07)]
        backdrop-blur-xl
        transition-all
        duration-300
        hover:-translate-x-0.5
        hover:bg-white
        hover:shadow-[0_12px_28px_rgba(76,122,46,0.12)]
      "
    >
      <span
        className="
          flex
          h-6
          w-6
          items-center
          justify-center
          rounded-full
          bg-[#e3ecd6]
          transition-transform
          duration-300
          group-hover:-translate-x-0.5
        "
      >
        ←
      </span>

      Back
    </button>
  </div>
  </div>
        <div
          className="
            mb-8
            flex
            flex-col
            gap-5
            sm:mb-10
            md:flex-row
            md:items-end
            md:justify-between
          "
        >
          <div>
            <p
              className="
                mb-2
                text-[10px]
                font-bold
                uppercase
                tracking-[0.18em]
                text-[#4c7a2e]
                sm:text-[11px]
              "
            >
              Your portfolio
            </p>

            <h1
              className="
                font-[Playfair_Display] text-[30px]
                font-extrabold
                leading-[1.1]
                tracking-[-0.04em]
                text-[#1f2318]
                sm:font-[Playfair_Display] text-[38px]
                lg:font-[Playfair_Display] text-[44px]
              "
            >
              Everything you own,
              <br className="hidden sm:block" />
              in one place
            </h1>

            <div className="mt-4 h-1 w-12 rounded-full bg-gradient-to-r from-[#1c6385] to-[#4c7a2e]" />
          </div>

          <div
            className="
              flex
              items-center
              gap-3
              rounded-full
              border
              border-white
              bg-white/72
              px-4
              py-2
              shadow-sm
              backdrop-blur-xl
            "
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e3ecd6] text-[#4c7a2e]">
              <Wallet size={14} />
            </div>

            <div>
              <p className="text-[8px] font-semibold uppercase tracking-[0.08em] text-[#7b8471]">
                Investor ID
              </p>

              <p className="text-[11px] font-bold text-[#1f2318]">
                {portfolio?.investor_id || "—"}
              </p>
            </div>
          </div>
        </div>

        {/* ===================================================
            SUMMARY CARDS
        =================================================== */}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Properties */}

          <div
            className="
              group
              rounded-[22px]
              border
              border-white/80
              bg-white/78
              p-5
              shadow-[0_12px_35px_rgba(76,122,46,0.07)]
              backdrop-blur-xl
              transition-all
              duration-300
              hover:-translate-y-1
              hover:shadow-[0_18px_40px_rgba(76,122,46,0.11)]
            "
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#8297a0]">
                  Properties owned
                </p>

                <p className="mt-2 text-[27px] font-extrabold tracking-[-0.04em] text-[#1f2318]">
                  {portfolioStats.totalProperties}
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#e3ecd6] text-[#4c7a2e]">
                <Building2 size={18} />
              </div>
            </div>

            <p className="mt-3 text-[10px] font-medium text-[#7b8471]">
              Active investment holdings
            </p>
          </div>

          {/* Blocks */}

          <div
            className="
              group
              rounded-[22px]
              border
              border-white/80
              bg-white/78
              p-5
              shadow-[0_12px_35px_rgba(76,122,46,0.07)]
              backdrop-blur-xl
              transition-all
              duration-300
              hover:-translate-y-1
              hover:shadow-[0_18px_40px_rgba(76,122,46,0.11)]
            "
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#8297a0]">
                  Total tokens
                </p>

                <p className="mt-2 text-[27px] font-extrabold tracking-[-0.04em] text-[#1f2318]">
                  {formatNumber(portfolioStats.totalBlocks)}
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#e3ecd6] text-[#4c7a2e]">
                <Wallet size={18} />
              </div>
            </div>

            <p className="mt-3 text-[10px] font-medium text-[#7b8471]">
              Across all properties
            </p>
          </div>

          {/* Ownership */}

          <div
            className="
              group
              rounded-[22px]
              border
              border-white/80
              bg-white/78
              p-5
              shadow-[0_12px_35px_rgba(76,122,46,0.07)]
              backdrop-blur-xl
              transition-all
              duration-300
              hover:-translate-y-1
              hover:shadow-[0_18px_40px_rgba(76,122,46,0.11)]
            "
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#8297a0]">
                  Total ownership
                </p>

                <p className="mt-2 text-[27px] font-extrabold tracking-[-0.04em] text-[#1f2318]">
                  {formatNumber(portfolioStats.totalOwnership)}%
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#e7eedf] text-[#6b8f4e]">
                <Percent size={18} />
              </div>
            </div>

            <p className="mt-3 text-[10px] font-medium text-[#7b8471]">
              Combined ownership percentage
            </p>
          </div>

          {/* Rent */}

          <div
            className="
              group
              rounded-[22px]
              border
              border-white/80
              bg-white/78
              p-5
              shadow-[0_12px_35px_rgba(76,122,46,0.07)]
              backdrop-blur-xl
              transition-all
              duration-300
              hover:-translate-y-1
              hover:shadow-[0_18px_40px_rgba(76,122,46,0.11)]
            "
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-[#8297a0]">
                  Rent earned
                </p>

                <p className="mt-2 text-[22px] font-extrabold tracking-[-0.035em] text-[#1f2318]">
                  AED {formatNumber(portfolioStats.totalRent)}
                </p>
              </div>

              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#f3eedc] text-[#8a9463]">
                <Banknote size={18} />
              </div>
            </div>

            <p className="mt-3 text-[10px] font-medium text-[#7b8471]">
              Total rent earned
            </p>
          </div>
        </div>

        {/* ===================================================
            HOLDINGS SECTION
        =================================================== */}

        <div
          className="
            mt-8
            overflow-hidden
            rounded-[26px]
            border
            border-white/80
            bg-white/72
            p-5
            shadow-[0_20px_55px_rgba(76,122,46,0.08)]
            backdrop-blur-xl
            sm:p-6
            lg:p-7
          "
        >
          {/* Heading */}

          <div className="mb-6 flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#4c7a2e]">
                Investment holdings
              </p>

              <h2 className="mt-1 text-[21px] font-extrabold tracking-[-0.025em] text-[#1f2318] sm:text-[24px]">
                Your properties
              </h2>
            </div>

            <div
              className="
                rounded-full
                border
                border-[#dfe6d7]
                bg-[#f7f8f1]
                px-3
                py-1.5
                text-[9px]
                font-bold
                text-[#66705c]
              "
            >
              {holdings.length}{" "}
              {holdings.length === 1 ? "Property" : "Properties"}
            </div>
          </div>

          {/* =================================================
              EMPTY STATE
          ================================================= */}

          {holdings.length === 0 ? (
            <div
              className="
                flex
                min-h-[260px]
                flex-col
                items-center
                justify-center
                rounded-[18px]
                border
                border-dashed
                border-[#d8e1cf]
                bg-[#fafbf6]/75
                px-5
                text-center
              "
            >
              <div
                className="
                  flex
                  h-14
                  w-14
                  items-center
                  justify-center
                  rounded-2xl
                  bg-[#e3ecd6]
                  text-[#4c7a2e]
                "
              >
                <Building2 size={24} />
              </div>

              <h3 className="mt-4 text-[17px] font-bold text-[#1f2318]">
                No properties yet
              </h3>

              <p className="mt-1.5 max-w-[350px] text-[11px] leading-[1.7] text-[#8297a0]">
                Your property investments will appear here once you make your
                first investment.
              </p>
            </div>
          ) : (
            /* =================================================
               HOLDINGS LIST
            ================================================= */

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {holdings.map((holding, index) => (
                <div
                  key={`${holding?.property_id || "property"}-${index}`}
                  className="
                    group
                    relative
                    overflow-hidden
                    rounded-[18px]
                    border
                    border-[#dfe6d7]
                    bg-[#fffefa]
                    p-5
                    transition-all
                    duration-300
                    hover:-translate-y-1
                    hover:border-[#cbdfe7]
                    hover:bg-white
                    hover:shadow-[0_15px_35px_rgba(76,122,46,0.10)]
                  "
                >
                  {/* Top */}

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div
                        className="
                          flex
                          h-11
                          w-11
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          bg-gradient-to-br
                          from-[#e3ecd6]
                          to-[#d7e3ca]
                          text-[#4c7a2e]
                        "
                      >
                        <Building2 size={19} />
                      </div>

                      <div className="min-w-0">
                        <p className="truncate text-[15px] font-bold tracking-[-0.02em] text-[#1f2318]">
                          {holding?.property_name || "Unnamed Property"}
                        </p>

                        <p className="mt-1 truncate text-[9px] font-medium text-[#8a9ba2]">
                          Property ID: {holding?.property_id || "—"}
                        </p>
                      </div>
                    </div>

                    <div
                      className="
                        flex
                        h-8
                        w-8
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        border
                        border-[#dfe6d7]
                        bg-white
                        text-[#7b8471]
                        transition-all
                        duration-300
                        group-hover:border-[#c5d6b9]
                        group-hover:text-[#4c7a2e]
                        cursor-pointer
                      "
                      onClick={()=>{
                        handlePortfolioDetails(holding?.property_id);
                      }}
                    >
                      <ChevronRight size={15} />
                    </div>
                  </div>

                  {/* Divider */}

                  <div className="my-5 h-px bg-[#e6ebdf]" />

                  {/* Details */}

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <p className="text-[8px] font-bold uppercase tracking-[0.08em] text-[#7b8471]">
                        Tokens held
                      </p>

                      <p className="mt-1.5 text-[16px] font-extrabold tracking-[-0.025em] text-[#1f2318]">
                        {formatNumber(holding?.blocks_held)}
                      </p>
                    </div>

                    <div>
                      <p className="text-[8px] font-bold uppercase tracking-[0.08em] text-[#7b8471]">
                        Ownership
                      </p>

                      <p className="mt-1.5 text-[16px] font-extrabold tracking-[-0.025em] text-[#1f2318]">
                        {formatNumber(holding?.ownership_pct)}%
                      </p>
                    </div>

                    <div>
                      <p className="text-[8px] font-bold uppercase tracking-[0.08em] text-[#7b8471]">
                        Rent earned
                      </p>

                      <p className="mt-1.5 text-[15px] font-extrabold tracking-[-0.025em] text-[#4c7a2e]">
                        {formatAED(holding?.rent_earned_aed)}
                      </p>
                    </div>
                  </div>

                  {/* Ownership progress */}

                  <div className="mt-5">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-[8px] font-medium text-[#7b8471]">
                        Ownership
                      </span>

                      <span className="text-[8px] font-bold text-[#66705c]">
                        {formatNumber(holding?.ownership_pct)}%
                      </span>
                    </div>

                    <div className="h-1.5 overflow-hidden rounded-full bg-[#e6ebdf]">
                      <div
                        className="
                          h-full
                          rounded-full
                          bg-gradient-to-r
                          from-[#6fa142]
                          to-[#4c7a2e]
                          transition-all
                          duration-700
                        "
                        style={{
                          width: `${Math.min(
                            Number(holding?.ownership_pct || 0),
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserPortFolio;

